/**
 * Auth & Eligibility -- 270/271 + prior auth check inside the workflow shell.
 *
 * Route: /AuthEligibility
 *
 * Supports two engines:
 *   v4 (default) -- real-time 270/271 + PA via payer adapter
 *   v5           -- natural-language Medicare LCD/NCD coverage check via SNN process map
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  WorkflowLayout,
  FileDropZone,
  ResultsPanel,
  Icon,
  ExampleModal,
  getWorkflowModule,
} from '../../components/workflow';
import { verifyEligibility, submitPriorAuth } from '../../services/suiteApi';
import { getSampleById } from '../../fixtures/testCases';
import { AUTH_ELIGIBILITY_EXAMPLE } from '../../fixtures/exampleInputs';
import './suite.css';

function summariseResult(form, elig, pa, sample) {
  if (!elig && !pa) return '';
  const lines = [`AUTH & ELIGIBILITY — ${sample ? `sample: ${sample.title}` : `member ${form.memberId}`}`];
  if (form.memberId) lines.push(`Member: ${form.firstName} ${form.lastName} (${form.memberId})`);
  if (form.dateOfBirth) lines.push(`DOB: ${form.dateOfBirth}`);
  if (elig) {
    lines.push('', '--- 270/271 ---');
    lines.push(`Active: ${elig.active ? 'YES' : 'NO'} (${elig.coverageStatusDescription || '—'})`);
    lines.push(`Plan: ${elig.planName || '—'} (${elig.payerName || '—'})`);
    lines.push(`Network: ${elig.network || 'unknown'}`);
    if (elig.effectiveDate || elig.terminationDate) {
      lines.push(`Effective: ${elig.effectiveDate || '—'} → ${elig.terminationDate || 'open'}`);
    }
    if ((elig.benefits || []).length) {
      lines.push(`Benefits: ${elig.benefits.slice(0, 4).map((b) => b.name).join(', ')}`);
    }
  }
  if (pa) {
    lines.push('', '--- Prior auth ---');
    lines.push(`Decision: ${String(pa.status || 'pended').toUpperCase()} (${pa.reason || '—'})`);
    lines.push(`Auth #: ${pa.authNumber || '—'} · Valid through ${pa.expirationDate || '—'}`);
  }
  return lines.join('\n');
}

// ----------- V5 Report Renderer -----------

const STATUS_COLORS = {
  BILL_READY: '#16a34a',
  BILL_READY_WITH_CONDITIONS: '#d97706',
  HOLD_FOR_REVIEW: '#d97706',
  NOT_BILLABLE: '#dc2626',
  NEEDS_ABN: '#9333ea',
};
const RISK_COLORS = { LOW: '#16a34a', MEDIUM: '#d97706', HIGH: '#dc2626', NOT_EVALUATED: '#6b7280', NOT_APPLICABLE: '#6b7280' };
const EVIDENCE_COLORS = { FOUND: '#16a34a', NOT_FOUND: '#dc2626', PARTIAL: '#d97706' };
const CROSSWALK_COLORS = { MET: '#16a34a', POTENTIALLY_MET: '#d97706', NOT_MET: '#dc2626', NEEDS_REVIEW: '#6b7280' };

function V5CoverageReport({ report }) {
  if (!report) return null;
  const status = report.overallStatus || 'HOLD_FOR_REVIEW';
  return (
    <div style={{ fontSize: 13 }}>
      {/* Executive Summary */}
      <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, border: '1px solid var(--dm-border)', background: 'var(--dm-bg-raised, #fafbfc)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{ padding: '3px 10px', borderRadius: 6, fontWeight: 700, fontSize: 11, color: '#fff', background: STATUS_COLORS[status] || '#666' }}>{status.replace(/_/g, ' ')}</span>
          <span style={{ fontSize: 12, color: 'var(--dm-muted)' }}>Confidence: <b>{report.confidence}%</b></span>
        </div>
        <div style={{ marginBottom: 6 }}>{report.summary}</div>
        {report.recommendedAction && <div style={{ fontSize: 12, color: '#1d4ed8' }}><b>Next:</b> {report.recommendedAction}</div>}
      </div>

      {/* Service Reviewed */}
      {report.serviceReviewed && (
        <details open style={{ marginBottom: 14 }}>
          <summary style={{ fontWeight: 700, cursor: 'pointer' }}>Service Reviewed</summary>
          <dl className="wf-kv" style={{ marginTop: 6 }}>
            <div><dt>CPT/HCPCS</dt><dd>{report.serviceReviewed.cptCode} — {report.serviceReviewed.cptDescription}</dd></div>
            <div><dt>Payer</dt><dd>{report.serviceReviewed.payerType} / {report.serviceReviewed.macJurisdiction}</dd></div>
            <div><dt>DOS</dt><dd>{report.serviceReviewed.dos}</dd></div>
            <div><dt>POS</dt><dd>{report.serviceReviewed.pos}</dd></div>
            <div><dt>Units</dt><dd>{report.serviceReviewed.units}</dd></div>
            <div><dt>ABN</dt><dd>{report.serviceReviewed.abnStatus}</dd></div>
            {report.serviceReviewed.priorTestDate && <div><dt>Prior Test</dt><dd>{report.serviceReviewed.priorTestDate}: {report.serviceReviewed.priorTestResult}</dd></div>}
          </dl>
          {(report.serviceReviewed.supportingDx || []).length > 0 && (
            <div style={{ marginTop: 6, fontSize: 12 }}>
              <b>Supporting Dx:</b> {report.serviceReviewed.supportingDx.map(d => `${d.code} (${d.description})`).join(', ')}
            </div>
          )}
        </details>
      )}

      {/* Policy Lookup */}
      {report.policyLookup && (
        <details style={{ marginBottom: 14 }}>
          <summary style={{ fontWeight: 700, cursor: 'pointer' }}>Policy Lookup ({report.policyLookup.status})</summary>
          {report.policyLookup.interpretation && <p style={{ margin: '6px 0', fontSize: 12 }}>{report.policyLookup.interpretation}</p>}
          {(report.policyLookup.articles || []).map((a, i) => (
            <div key={i} style={{ marginTop: 8, padding: 8, border: '1px solid var(--dm-border)', borderRadius: 6, fontSize: 12 }}>
              <b>{a.type}: {a.articleId}</b> — {a.title}
              {a.frequencyRule && <div style={{ marginTop: 4 }}><b>Frequency:</b> {a.frequencyRule}</div>}
              {(a.coveredIndications || []).length > 0 && <div style={{ marginTop: 4 }}><b>Covered:</b> {a.coveredIndications.join('; ')}</div>}
              {(a.documentationRequirements || []).length > 0 && <div style={{ marginTop: 4 }}><b>Documentation:</b> {a.documentationRequirements.join('; ')}</div>}
              {(a.nonCoveredIndications || []).length > 0 && <div style={{ marginTop: 4, color: '#dc2626' }}><b>Non-covered:</b> {a.nonCoveredIndications.join('; ')}</div>}
            </div>
          ))}
        </details>
      )}

      {/* Clinical Evidence */}
      {(report.clinicalEvidence || []).length > 0 && (
        <details style={{ marginBottom: 14 }}>
          <summary style={{ fontWeight: 700, cursor: 'pointer' }}>Clinical Evidence ({report.clinicalEvidence.length} items)</summary>
          <table style={{ width: '100%', fontSize: 12, marginTop: 6, borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--dm-border)' }}>
              <th style={{ textAlign: 'left', padding: 4 }}>Item</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Status</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Found</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Why It Matters</th>
            </tr></thead>
            <tbody>
              {report.clinicalEvidence.map((e, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--dm-border)' }}>
                  <td style={{ padding: 4, fontWeight: 600 }}>{e.item}</td>
                  <td style={{ padding: 4, color: EVIDENCE_COLORS[e.status] || '#666', fontWeight: 700, fontSize: 11 }}>{e.status}</td>
                  <td style={{ padding: 4 }}>{e.found}</td>
                  <td style={{ padding: 4, color: 'var(--dm-muted)' }}>{e.whyItMatters}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {/* Evidence-to-Coverage Crosswalk */}
      {(report.evidenceToCoverageCrosswalk || []).length > 0 && (
        <details style={{ marginBottom: 14 }}>
          <summary style={{ fontWeight: 700, cursor: 'pointer' }}>Evidence-to-Coverage Crosswalk</summary>
          <table style={{ width: '100%', fontSize: 12, marginTop: 6, borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--dm-border)' }}>
              <th style={{ textAlign: 'left', padding: 4 }}>Requirement</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Status</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Evidence</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Explanation</th>
            </tr></thead>
            <tbody>
              {report.evidenceToCoverageCrosswalk.map((c, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--dm-border)' }}>
                  <td style={{ padding: 4, fontWeight: 600 }}>{c.requirement}</td>
                  <td style={{ padding: 4, color: CROSSWALK_COLORS[c.status] || '#666', fontWeight: 700, fontSize: 11 }}>{c.status?.replace(/_/g, ' ')}</td>
                  <td style={{ padding: 4 }}>{c.evidence}</td>
                  <td style={{ padding: 4, color: 'var(--dm-muted)' }}>{c.explanation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {/* Diagnosis Support */}
      {(report.diagnosisSupport || []).length > 0 && (
        <details style={{ marginBottom: 14 }}>
          <summary style={{ fontWeight: 700, cursor: 'pointer' }}>Diagnosis Support</summary>
          <table style={{ width: '100%', fontSize: 12, marginTop: 6, borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--dm-border)' }}>
              <th style={{ textAlign: 'left', padding: 4 }}>Code</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Description</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Chart Support</th>
              <th style={{ textAlign: 'left', padding: 4 }}>On Covered List</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Assessment</th>
            </tr></thead>
            <tbody>
              {report.diagnosisSupport.map((d, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--dm-border)' }}>
                  <td style={{ padding: 4, fontWeight: 600 }}>{d.code}</td>
                  <td style={{ padding: 4 }}>{d.description}</td>
                  <td style={{ padding: 4 }}>{d.chartSupport}</td>
                  <td style={{ padding: 4 }}>{d.onCoveredList === true ? 'Yes' : d.onCoveredList === false ? 'No' : 'Unknown'}</td>
                  <td style={{ padding: 4, fontWeight: 600 }}>{d.assessment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {/* Billing Risk */}
      {report.billingRisk && (
        <details style={{ marginBottom: 14 }}>
          <summary style={{ fontWeight: 700, cursor: 'pointer' }}>Billing Risk Assessment</summary>
          <dl className="wf-kv" style={{ marginTop: 6 }}>
            {['medicalNecessity', 'diagnosisSupport', 'frequency', 'abn', 'eligibility', 'priorAuth'].map(key => {
              const risk = report.billingRisk[`${key}Risk`];
              const reason = report.billingRisk[`${key}Reason`];
              if (!risk) return null;
              return (
                <div key={key}>
                  <dt style={{ textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1').trim()}</dt>
                  <dd><span style={{ color: RISK_COLORS[risk] || '#666', fontWeight: 700 }}>{risk}</span>{reason ? ` — ${reason}` : ''}</dd>
                </div>
              );
            })}
          </dl>
        </details>
      )}

      {/* Pre-Bill Decision */}
      {report.preBillDecision && (
        <details style={{ marginBottom: 14 }}>
          <summary style={{ fontWeight: 700, cursor: 'pointer' }}>Pre-Bill Decision</summary>
          <div style={{ marginTop: 6 }}>
            <div style={{ marginBottom: 6 }}><b>Decision:</b> <span style={{ color: STATUS_COLORS[report.preBillDecision.decision] || '#666' }}>{(report.preBillDecision.decision || '').replace(/_/g, ' ')}</span></div>
            {(report.preBillDecision.nextActions || []).length > 0 && (
              <div style={{ marginBottom: 6 }}>
                <b>Next Actions:</b>
                <ol style={{ margin: '4px 0', paddingLeft: 20, fontSize: 12 }}>
                  {report.preBillDecision.nextActions.map((a, i) => <li key={i}>{a}</li>)}
                </ol>
              </div>
            )}
            {(report.preBillDecision.doNot || []).length > 0 && (
              <div>
                <b>Do Not:</b>
                <ul style={{ margin: '4px 0', paddingLeft: 20, fontSize: 12, color: '#dc2626' }}>
                  {report.preBillDecision.doNot.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </div>
            )}
          </div>
        </details>
      )}

      {/* Documentation Packet */}
      {(report.documentationPacket || []).length > 0 && (
        <details style={{ marginBottom: 14 }}>
          <summary style={{ fontWeight: 700, cursor: 'pointer' }}>Documentation Packet</summary>
          <ul style={{ margin: '6px 0', paddingLeft: 20, fontSize: 12 }}>
            {report.documentationPacket.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        </details>
      )}

      {/* NL Analysis */}
      {report.naturalLanguageAnalysis && (
        <details style={{ marginBottom: 14 }}>
          <summary style={{ fontWeight: 700, cursor: 'pointer' }}>Detailed Analysis</summary>
          <div style={{ whiteSpace: 'pre-wrap', marginTop: 6, lineHeight: 1.5, color: 'var(--dm-ink)' }}>
            {report.naturalLanguageAnalysis}
          </div>
        </details>
      )}
    </div>
  );
}

// ----------- Main Page Component -----------

export default function AuthEligibilityPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const moduleDef = getWorkflowModule('auth-eligibility');

  const [engine, setEngine] = useState('v5');
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({
    memberId: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    dateOfService: '',
    serviceTypeCode: '30',
  });
  const [clinicalText, setClinicalText] = useState('');
  const [showExample, setShowExample] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busySampleId, setBusySampleId] = useState(null);
  const [error, setError] = useState('');
  const [elig, setElig] = useState(null);
  const [v5Report, setV5Report] = useState(null);
  const [pa, setPa] = useState(null);
  const [paBusy, setPaBusy] = useState(false);
  const [paForm, setPaForm] = useState({ cptCode: '', diagnosisCodes: '', units: '1', serviceFromDate: '', serviceToDate: '', renderingNpi: '' });
  const [showPaForm, setShowPaForm] = useState(false);
  const [sample, setSample] = useState(null);
  const printRef = useRef(null);

  const sampleId = searchParams.get('sample');

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const run = async (overrides, ctOverride) => {
    setBusy(true); setError(''); setElig(null); setV5Report(null);
    const textVal = ctOverride ?? clinicalText;
    try {
      if (engine === 'v5' && textVal.trim()) {
        const data = await verifyEligibility({ engine: 'v5', clinicalText: textVal.trim() });
        if (!data?.success) throw new Error(data?.message || 'Coverage check failed');
        setV5Report(data.data);
      } else {
        const data = await verifyEligibility({ ...form, ...(overrides || {}) });
        if (!data?.success) throw new Error(data?.message || 'Eligibility failed');
        setElig(data.data);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const runPa = async () => {
    setPaBusy(true); setError('');
    try {
      const dxCodes = (paForm.diagnosisCodes || '').split(',').map(s => s.trim()).filter(Boolean);
      const data = await submitPriorAuth({
        memberId: form.memberId,
        cptCode: paForm.cptCode || '72148',
        units: Number(paForm.units) || 1,
        serviceFromDate: paForm.serviceFromDate || form.dateOfService || '',
        serviceToDate: paForm.serviceToDate || form.dateOfService || '',
        diagnosisCodes: dxCodes.length ? dxCodes : ['M54.5'],
        renderingNpi: paForm.renderingNpi || undefined,
      });
      if (!data?.success) throw new Error(data?.message || 'PA failed');
      setPa(data.data);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setPaBusy(false);
    }
  };

  const onPickSample = async (s) => {
    setBusySampleId(s.id);
    setSample(s);
    const next = { ...form, ...s.payload };
    setForm(next);
    if (s.payload.clinicalText) setClinicalText(s.payload.clinicalText);
    await run(s.payload, s.payload.clinicalText);
    setBusySampleId(null);
  };

  useEffect(() => {
    if (!sampleId) return;
    const s = getSampleById(sampleId);
    if (s && s.moduleId === 'auth-eligibility') onPickSample(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleId]);

  const onRun = () => {
    setSample(null);
    if (sampleId) setSearchParams({});
    run();
  };

  const currentStep = (elig || v5Report) ? 'results' : (form.memberId || clinicalText.trim() ? 'run' : 'choose');
  const chatContext = useMemo(() => summariseResult(form, elig, pa, sample), [form, elig, pa, sample]);

  return (
    <WorkflowLayout
      module={moduleDef}
      currentStep={currentStep}
      caseContext={chatContext}
    >
      <section className="wf-step-card">
        <header className="wf-step-card__head">
          <h2 className="wf-step-card__title">
            <span className="wf-step-card__num">2</span>
            {engine === 'v5' ? 'Clinical text' : 'Member details'}
          </h2>
        </header>

        {engine === 'v5' && (
          <div style={{ marginBottom: 12 }}>
            <p className="wf-step-card__hint">Paste the full clinical note with service details, CPT/HCPCS, ICD-10 codes, prior test history, and payer information.</p>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--dm-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Clinical Note / Service Details
              </label>
              <button type="button" className="example-link" onClick={() => setShowExample(true)}>View Example</button>
            </div>
            <textarea
              value={clinicalText}
              onChange={(e) => setClinicalText(e.target.value)}
              placeholder="Paste the full clinical text including payer type, CPT/HCPCS, ICD-10 diagnoses, prior test history, and clinical documentation..."
              rows={10}
              style={{ marginTop: 6, width: '100%', padding: 10, border: '1px solid var(--dm-border)', borderRadius: 8, fontSize: 13, color: 'var(--dm-ink)', fontWeight: 400, textTransform: 'none', letterSpacing: 0, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5, boxSizing: 'border-box' }}
            />
          </div>
        )}

        {engine === 'v4' && (
          <>
            <p className="wf-step-card__hint">
              {moduleDef.uploadHint} Demo scenarios: <code>TERM</code> = terminated coverage,{' '}
              <code>OON</code> = out-of-network, <code>INACT</code> = inactive.
            </p>
            <FileDropZone
              files={files}
              onFiles={setFiles}
              accept={moduleDef.accepts}
              multiple
              hint="270/271 EDI, existing PA letters, or coverage screenshots."
            />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginTop: 10 }}>
              {[
                { k: 'memberId', l: 'Member ID' },
                { k: 'firstName', l: 'First name' },
                { k: 'lastName', l: 'Last name' },
                { k: 'dateOfBirth', l: 'Date of birth' },
                { k: 'dateOfService', l: 'Date of service' },
                { k: 'serviceTypeCode', l: 'Service type code' },
              ].map((f) => (
                <label key={f.k} style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--dm-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {f.l}
                  <input
                    value={form[f.k]}
                    onChange={(e) => setField(f.k, e.target.value)}
                    style={{ padding: 9, border: '1px solid var(--dm-border)', borderRadius: 8, fontSize: 14, color: 'var(--dm-ink)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}
                  />
                </label>
              ))}
            </div>
          </>
        )}

        <div className="wf-actions">
          <button type="button" className="wf-btn" disabled={busy || !clinicalText.trim()} onClick={onRun}>
            <Icon.play /> {busy ? 'Checking…' : (engine === 'v5' ? 'Run Coverage Check' : moduleDef.runLabel)}
          </button>
          {engine === 'v4' && (
            <button type="button" className="wf-btn wf-btn--ghost" disabled={!elig} onClick={() => setShowPaForm((v) => !v)}>
              {showPaForm ? 'Hide PA form' : 'Submit prior auth'}
            </button>
          )}
        </div>
        {engine === 'v4' && showPaForm && (
          <div style={{ marginTop: 12, padding: 12, border: '1px solid var(--dm-border)', borderRadius: 8, background: 'var(--dm-bg-raised, #fafbfc)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--dm-muted)', marginBottom: 8 }}>Prior authorization request</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
              {[
                { k: 'cptCode', l: 'CPT code', ph: 'e.g. 72148' },
                { k: 'diagnosisCodes', l: 'Diagnosis codes (comma-sep)', ph: 'M54.5, G89.4' },
                { k: 'units', l: 'Units', ph: '1' },
                { k: 'serviceFromDate', l: 'Service from date', ph: 'YYYY-MM-DD' },
                { k: 'serviceToDate', l: 'Service to date', ph: 'YYYY-MM-DD' },
                { k: 'renderingNpi', l: 'Rendering NPI (optional)', ph: '1234567890' },
              ].map((f) => (
                <label key={f.k} style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--dm-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {f.l}
                  <input
                    value={paForm[f.k]}
                    placeholder={f.ph}
                    onChange={(e) => setPaForm((prev) => ({ ...prev, [f.k]: e.target.value }))}
                    style={{ padding: 8, border: '1px solid var(--dm-border)', borderRadius: 6, fontSize: 13, color: 'var(--dm-ink)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}
                  />
                </label>
              ))}
            </div>
            <button type="button" className="wf-btn" disabled={paBusy || !paForm.cptCode} onClick={runPa} style={{ marginTop: 10 }}>
              {paBusy ? 'Submitting…' : 'Submit PA request'}
            </button>
          </div>
        )}
        {error && <div className="wf-error">{error}</div>}
      </section>

      {v5Report && (
        <ResultsPanel
          title="Medicare LCD/NCD Coverage Report (v5)"
          data={v5Report}
          downloadName={`auth-eligibility-v5_${sample?.id || 'check'}`}
          textReport={v5Report.naturalLanguageAnalysis || ''}
          printableRef={printRef}
        >
          <div ref={printRef}>
            <V5CoverageReport report={v5Report} />
          </div>
        </ResultsPanel>
      )}

      {(elig || pa) && !v5Report && (
        <ResultsPanel
          title="Eligibility / Prior auth"
          data={{ eligibility: elig, priorAuth: pa }}
          downloadName={`auth-eligibility_${sample?.id || form.memberId}`}
          textReport={chatContext}
          printableRef={printRef}
        >
          <div ref={printRef}>
            {elig && (
              <div style={{ marginBottom: 16 }}>
                {!elig.active && (
                  <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8, background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', fontSize: 13, fontWeight: 600 }}>
                    DO NOT SUBMIT — coverage is not active{elig.terminationDate ? ` (terminated ${elig.terminationDate})` : ''}. Verify with member before filing claims for this DOS.
                  </div>
                )}
                {elig.network === 'out_of_network' && (
                  <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8, background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', fontSize: 13, fontWeight: 600 }}>
                    OUT-OF-NETWORK — Member has active coverage but this provider/service is out-of-network. OON benefits apply; higher patient responsibility expected.
                  </div>
                )}
                {(elig.secondaryPayer || elig.cobIndicator || elig.coordinationOfBenefits) && (
                  <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8, background: '#eff6ff', border: '1px solid #93c5fd', color: '#1e40af', fontSize: 13, fontWeight: 600 }}>
                    COB DETECTED — {elig.secondaryPayer?.payerName || elig.coordinationOfBenefits?.otherPayerName || 'Secondary payer'} is on file. Verify payer order before filing to avoid CARC 22/23/109 denials.
                  </div>
                )}

                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--dm-muted)', marginBottom: 6 }}>
                  270 / 271
                </div>
                <dl className="wf-kv">
                  <div><dt>Coverage</dt><dd><span className={`suite-badge ${elig.active ? 'suite-badge--ok' : 'suite-badge--bad'}`}>{elig.active ? 'ACTIVE' : 'INACTIVE'}</span></dd></div>
                  {elig.coverageStatusDescription && (
                    <div><dt>Status detail</dt><dd>{elig.coverageStatusDescription}</dd></div>
                  )}
                  <div><dt>Plan</dt><dd>{elig.planName || '—'}</dd></div>
                  <div><dt>Payer</dt><dd>{elig.payerName || '—'}</dd></div>
                  <div><dt>Network</dt><dd>{String(elig.network || 'unknown').replace(/_/g, ' ')}</dd></div>
                  {elig.groupNumber && (
                    <div><dt>Group #</dt><dd>{elig.groupNumber}{elig.groupName ? ` (${elig.groupName})` : ''}</dd></div>
                  )}
                  {elig.subscriberRelationship && (
                    <div><dt>Relationship</dt><dd>{elig.subscriberRelationship}</dd></div>
                  )}
                  <div><dt>Effective</dt><dd>{elig.effectiveDate || '—'}</dd></div>
                  <div><dt>Termination</dt><dd>{elig.terminationDate || 'open'}</dd></div>
                </dl>

                {(elig.benefits || []).length > 0 && (
                  <table className="suite-table" style={{ marginTop: 12 }}>
                    <thead>
                      <tr><th>Benefit</th><th>Type</th><th>Copay / Coins</th><th>Deductible remaining</th><th>OOP remaining</th><th>Network</th></tr>
                    </thead>
                    <tbody>
                      {elig.benefits.map((b, idx) => (
                        <tr key={idx}>
                          <td><b>{b.name}</b></td>
                          <td>{b.serviceType || b.serviceTypeCode || '—'}</td>
                          <td>
                            {b.benefitAmount != null ? `$${b.benefitAmount} copay` : ''}
                            {b.benefitPercent != null ? `${Math.round(Number(b.benefitPercent) * 100)}% coinsurance` : ''}
                            {!b.benefitAmount && !b.benefitPercent ? '—' : ''}
                          </td>
                          <td>{b.deductibleRemaining != null ? `$${b.deductibleRemaining}` : b.remainingBenefitAmount != null ? `$${b.remainingBenefitAmount}` : '—'}</td>
                          <td>{b.oopRemaining != null ? `$${b.oopRemaining}` : '—'}</td>
                          <td>{b.inPlanNetworkIndicator ? 'In-network' : 'Out-of-network'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {pa && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--dm-muted)', marginBottom: 6 }}>
                  Prior auth decision
                </div>
                <dl className="wf-kv">
                  <div><dt>Decision</dt><dd><span className={`suite-badge ${pa.status === 'approved' ? 'suite-badge--ok' : pa.status === 'denied' ? 'suite-badge--bad' : 'suite-badge--warn'}`}>{String(pa.status || 'pended').toUpperCase()}</span></dd></div>
                  <div><dt>Auth #</dt><dd>{pa.authNumber || '—'}</dd></div>
                  <div><dt>CPT</dt><dd>{pa.cptCode || '—'}</dd></div>
                  <div><dt>Units</dt><dd>{pa.approvedUnits ?? 0} approved / {pa.requestedUnits ?? 0} requested</dd></div>
                  <div><dt>Valid through</dt><dd>{pa.expirationDate || '—'}</dd></div>
                  {pa.decisionDate && <div><dt>Decision date</dt><dd>{pa.decisionDate}</dd></div>}
                </dl>
                {pa.reason && <p style={{ margin: '6px 0 0', fontSize: 13, color: pa.status === 'denied' ? '#991b1b' : 'var(--dm-ink)' }}>{pa.reason}</p>}
              </div>
            )}
          </div>
        </ResultsPanel>
      )}
      <ExampleModal open={showExample} onClose={() => setShowExample(false)} title="Example: Auth & Eligibility Input" content={AUTH_ELIGIBILITY_EXAMPLE} />
    </WorkflowLayout>
  );
}
